/**
 * The closed `Priority.kind` vocabulary (#14995, ADR-0126 § 1).
 *
 * A kind exists only where the system BEHAVES differently — injection into
 * agent context, review cadence, completion semantics or rating semantics.
 * Labels that only change display (rock, project, pillar, goal) are not
 * kinds: a rock and a project are both `outcome`, and `pillar | goal |
 * strategy` stays on plan membership (`PLAN_PRIORITY_NODE_KINDS`, ADR-0099).
 * A task is its own kind, `task` (ADR-0126 § 1a): it is never rated, never
 * in a rating round, and an agent loads it as an action, not an objective.
 *
 * What each kind DOES is declared once, in `priority-kind-registry.ts`. This
 * module is only the list, so it stays importable from anywhere — a server
 * action, a client component, a prompt, a backfill — with nothing but zod
 * (ADR-0107; the exemplar is `context-research/plan-focus-tags.ts`). The
 * column is a `String`, not a Postgres enum, and this tuple is its only copy.
 *
 * `horizon` is NOT a kind test. It stays an attribute, parsed by
 * `priority-horizon.ts`, that drives review cadence within `outcome`.
 */

import { z } from 'zod'

/**
 * The eight kinds, in ADR-0126's order:
 *
 *  - `anchor`         purpose, mission, vision
 *  - `value`          core values
 *  - `principle`      strategic axioms, policies, guidelines
 *  - `responsibility` an area of responsibility, a seat
 *  - `practice`       a standing meeting, a procedure, a service commitment
 *  - `measure`        a yardstick
 *  - `outcome`        pillar, goal, rock, project
 *  - `task`           a to-do (ADR-0126 § 1a, #15013)
 */
export const PRIORITY_KINDS = [
  'anchor',
  'value',
  'principle',
  'responsibility',
  'practice',
  'measure',
  'outcome',
  'task',
] as const

export type PriorityKind = (typeof PRIORITY_KINDS)[number]

/** The validator half of the closed list; derived, never retyped. */
export const priorityKindSchema = z.enum(PRIORITY_KINDS)

export function isPriorityKind(value: unknown): value is PriorityKind {
  return priorityKindSchema.safeParse(value).success
}

/**
 * THE task test (ADR-0126 § 1a, #15013): a Priority is a task when its kind
 * is `task`. Never a horizon test — an undated task and an undated strategic
 * outcome both have `horizon = NULL`.
 */
export function isTaskKind(kind: string): boolean {
  return kind === 'task'
}

/**
 * Each kind by name, for a writer that mints a known kind:
 * `kind: PRIORITY_KIND.outcome` is a typo-proof literal, where a bare
 * `'outcome'` against the `String` column would compile with any spelling.
 */
export const PRIORITY_KIND: { readonly [K in PriorityKind]: K } = Object.freeze(
  {
    anchor: 'anchor',
    value: 'value',
    principle: 'principle',
    responsibility: 'responsibility',
    practice: 'practice',
    measure: 'measure',
    outcome: 'outcome',
    task: 'task',
  },
)
